import React, { useState, useEffect, useRef } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const AutoScrollCarousel = ({ title, videos, onVideoClick }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const containerRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);

  const scroll = (direction) => {
    if (containerRef.current) {
      const scrollAmount = 300;
      const newPosition = direction === 'left' 
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;
      
      containerRef.current.scrollLeft = newPosition;
      setScrollPosition(newPosition);
      setAutoPlay(false);
      
      // Resume auto-play after 5 seconds of no interaction
      setTimeout(() => setAutoPlay(true), 5000);
    }
  };

  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
    setAutoPlay(false);
    
    // Resume auto-play after user stops scrolling
    if (autoPlayIntervalRef.current) {
      clearTimeout(autoPlayIntervalRef.current);
    }
    autoPlayIntervalRef.current = setTimeout(() => {
      setAutoPlay(true);
    }, 3000);
  };

  // Auto-scroll effect
  useEffect(() => {
    if (!autoPlay || !containerRef.current) return;

    const interval = setInterval(() => {
      if (containerRef.current) {
        const container = containerRef.current;
        const maxScroll = container.scrollWidth - container.clientWidth;
        
        if (container.scrollLeft >= maxScroll - 50) {
          // Reset to beginning when reaching end
          container.scrollLeft = 0;
          setScrollPosition(0);
        } else {
          const newPosition = container.scrollLeft + 300;
          container.scrollLeft = newPosition;
          setScrollPosition(newPosition);
        }
      }
    }, 3000); // Auto-scroll every 3 seconds

    return () => clearInterval(interval);
  }, [autoPlay, videos.length]);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between px-4 sm:px-0">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{title}</h2>
        <span className="text-xs sm:text-sm text-gray-400 bg-gray-800 px-3 py-1 rounded-full">
          {autoPlay ? '▶ Auto-scrolling' : '⏸ Paused'}
        </span>
      </div>
      
      <div className="relative group -mx-4 sm:-mx-8 px-4 sm:px-8">
        <div
          ref={containerRef}
          onScroll={handleScroll}
          className="flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide"
          style={{ scrollBehavior: 'smooth' }}
        >
          {videos && videos.length > 0 ? (
            videos.map((video) => (
              <div
                key={video.id}
                onClick={() => onVideoClick(video.id)}
                className="flex-shrink-0 cursor-pointer group/card"
              >
                {/* Horizontal Banner Card */}
                <div className="relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <img
                    src={video.poster}
                    alt={video.title}
                    className="w-56 sm:w-64 md:w-80 h-32 sm:h-40 md:h-48 object-cover transition-transform duration-300 group-hover/card:brightness-110"
                  />
                  
                  {/* Dark Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />

                  {/* Content Overlay */}
                  <div className="absolute inset-0 flex flex-col justify-end p-2 sm:p-4">
                    <p className="text-white text-xs sm:text-sm font-bold line-clamp-2 leading-tight">
                      {video.title}
                    </p>
                  </div>

                  {/* Hover Action Button */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 bg-black/40">
                    <button className="bg-white/90 hover:bg-white text-black px-4 sm:px-6 py-1 sm:py-2 rounded-full font-bold text-xs sm:text-sm transition-all">
                      Watch
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="w-full flex items-center justify-center py-12 text-gray-500">
              No videos available
            </div>
          )}
        </div>

        {/* Left Arrow */}
        {scrollPosition > 0 && (
          <button
            onClick={() => scroll('left')}
            className="absolute -left-3 sm:-left-6 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-black p-1 sm:p-2 rounded-full transition-all duration-200 shadow-lg opacity-0 group-hover:opacity-100"
          >
            <FiChevronLeft size={20} className="sm:w-6 sm:h-6" />
          </button>
        )}

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          className="absolute -right-3 sm:-right-6 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-black p-1 sm:p-2 rounded-full transition-all duration-200 shadow-lg opacity-0 group-hover:opacity-100"
        >
          <FiChevronRight size={20} className="sm:w-6 sm:h-6" />
        </button>
      </div>
    </div>
  );
};

export default AutoScrollCarousel;
